import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { DataGridPremium, GridToolbar } from '@mui/x-data-grid-premium';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from '../../state/slices/snackBarSlice';

/** Internal Components. */
import RefDialog from './RefDialog';
import { callAPI } from '../../api/useApiRequest';

export default function AccountTable(props) {
    /** Get props from Account.js. */
    const { accounts, setAccounts, reference, setDeleted, email, getAccessTokenSilently } = props;

    const dispatch = useDispatch();

    /** Data for Account Info Datagrid row. */
    const [acctRow, setAcctRow] = useState([]);
    /** State for a selected row Id value. */
    const [selectedId, setSelectedId] = useState('');
    /** State for managing Dialog. */
    const [refDialogOpen, setRefDialogOpen] = useState(false);

    /**
     * Opens the reference dialog when a row is clicked.
     *  @param {Event} event - The click event.
     *  @param {number} id - The id of the clicked row.
     */
    const handleClickOpen = (event, id) => {
        event.preventDefault();
        setRefDialogOpen(true);
        setSelectedId(id);
    };

    const handleClickDelete = (event, id) => {
        event.preventDefault();
        if (window.confirm('Are you sure you want to delete the user data?')) {
            const del_info = acctRow.filter((row) => row.id == id).map(({ id }) => ({ id }));
            /** POST request to DB */
            // apiRequest('accounts', 'account_delete', email, del_info)
            //     .then((response) => {
            //         if (response.data['body-json'].statusCode === 200) {
            //             setSnackbar({
            //                 open: true,
            //                 message: response.data['body-json'].body.message,
            //                 severity: 'success',
            //             });
            //             setAcctRow(acctRow.filter((row) => row.id !== id));
            //         } else {
            //             setSnackbar({
            //                 open: true,
            //                 message: response.data['body-json'].body.message,
            //                 severity: 'error',
            //             });
            //         }
            //     })
            //     .catch((error) => {
            //         setSnackbar({
            //             open: true,
            //             message: 'Something went wrong. Please try again.',
            //             severity: 'error',
            //         });
            //     });
            callAPI({
                method: 'post',
                module: 'accounts',
                component: 'account_delete',
                getAccessTokenSilently,
                email: email,
                obj: del_info,
                onSuccess: (resp) => {
                    setAcctRow(acctRow.filter((row) => row.id !== id));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Account has been successfully deleted.',
                            severity: 'success',
                        }),
                    );
                },
                onError: () => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong. Please try again.',
                            severity: 'error',
                        }),
                    );
                },
            });
        }
    };

    /**
     * Maps the accounts data to a new format for datagrid row and sorts it by ID.
     * This effect is triggered whenever the accounts data changes.
     */
    useEffect(() => {
        const accountRow = [...accounts].map((item) => {
            const references = item.filteration.map((obj) => {
                return obj.name;
            });
            return {
                id: item.id,
                exist: item.exist,
                email: item.email,
                company_name: item.company_name,
                company_position: item.company_position,
                ref: references.join(', '),
                register_date: item.register_date,
                name: item.name,
                total: item.total,
                service_name: item.service_name,
            };
        });
        const sorted_accountRow = accountRow.sort((a, b) => a.id - b.id);
        setAcctRow(sorted_accountRow);
    }, [accounts]);

    /** Define Account info Datagrid Fields. */
    const acct_col = [
        { field: 'id', headerName: 'ID', align: 'center', headerAlign: 'center', flex: 0.2 },
        { field: 'exist', headerName: 'Exist', align: 'center', headerAlign: 'center', flex: 0.2, hide: true },
        {
            field: 'email',
            headerName: 'Account ID',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'company_name',
            headerName: 'Company Name',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'company_position',
            headerName: 'Position',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'ref',
            headerName: 'Sharing',
            flex: 1.3,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            flex: 0.2,
            cellClassName: 'actions',
            align: 'center',
            headerAlign: 'center',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<AddCircleOutlineIcon />}
                        label="References"
                        onClick={(event) => handleClickOpen(event, id)}
                        color="inherit"
                    />,
                ];
            },
        },
        {
            field: 'register_date',
            headerName: 'Registered Date',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'total',
            headerName: 'No. of Connection',
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'service_name',
            headerName: 'Service Name',
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'delete',
            type: 'actions',
            headerName: 'Delete',
            width: 80,
            cellClassName: 'actions',
            align: 'center',
            headerAlign: 'center',
            getActions: ({ row }) => {
                return [
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="acccount_delete"
                        onClick={(event) => handleClickDelete(event, row.id)}
                        color="inherit"
                        disabled={row.exist}
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <DataGridPremium
                rows={acctRow}
                columns={acct_col}
                density="compact"
                disableRowSelectionOnClick
                unstable_cellSelection
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            exist: false,
                        },
                    },
                    pagination: { paginationModel: { pageSize: 25 } },
                }}
                pagination={true}
                pageSizeOptions={[25, 50, 100]}
                sx={{ fontSize: 13, height: '82vh', border: 'none' }}
            />
            <RefDialog
                email={email}
                getAccessTokenSilently={getAccessTokenSilently}
                accounts={accounts}
                setAccounts={setAccounts}
                reference={reference}
                refDialogOpen={refDialogOpen}
                setRefDialogOpen={setRefDialogOpen}
                selectedId={selectedId}
                setDeleted={setDeleted}
            />
        </>
    );
}
